import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Phone as PhoneIcon, Lock as LockIcon, Visibility, VisibilityOff, AlternateEmail as AlternateEmailIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import {
  formatLoginIdentifierInput,
  getLoginIdentifierValidationError,
} from '../utils/validation';
import AuthShell, { authFormSurfaceSx } from './AuthShell';
import { APP_DISPLAY_NAME, pageTitle } from '../config/brand';

const Login: React.FC = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [identifierError, setIdentifierError] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const prev = document.title;
    document.title = pageTitle();
    return () => {
      document.title = prev;
    };
  }, []);

  useEffect(() => {
    if (identifier) {
      setFailedAttempts(0);
      setShowForgotPassword(false);
    }
  }, [identifier]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!identifier.trim() || !password) {
      setError('Please fill in all fields');
      return;
    }

    const idErr = getLoginIdentifierValidationError(identifier);
    setIdentifierError(idErr);
    if (idErr) {
      setError(idErr);
      return;
    }

    try {
      setError('');
      setLoading(true);
      const userData = await login(identifier.trim(), password);
      handleSuccessfulLogin();
      navigate(
        userData.role === 'SUPER_ADMIN'
          ? '/super-admin-dashboard'
          : userData.userType === 'CLIENT'
            ? '/explore'
            : userData.userType === 'VENDOR'
              ? '/vendor-dashboard'
              : userData.role === 'ADMIN'
                ? '/dashboard'
                : '/explore'
      );
    } catch (err: unknown) {
      console.error('Login error:', err);
      const axiosErr = err as {
        code?: string;
        message?: string;
        response?: { status?: number; data?: { error?: string } };
      };

      const serverMsg = axiosErr.response?.data?.error?.trim();

      if (axiosErr.code === 'ECONNABORTED' || axiosErr.message?.includes('timeout')) {
        setError('Connection timeout. Please check your internet connection and try again.');
      } else if (axiosErr.code === 'ERR_NETWORK' || axiosErr.message?.includes('Network Error')) {
        setError('Network error. Please check if the backend server is running and accessible.');
      } else if (serverMsg) {
        setError(serverMsg);
        const lower = serverMsg.toLowerCase();
        if (lower.includes('incorrect password')) {
          const newFailedAttempts = failedAttempts + 1;
          setFailedAttempts(newFailedAttempts);
          if (newFailedAttempts >= 3) {
            setShowForgotPassword(true);
          }
        }
      } else if (axiosErr.response?.status === 401 || axiosErr.response?.status === 400) {
        const newFailedAttempts = failedAttempts + 1;
        setFailedAttempts(newFailedAttempts);
        if (newFailedAttempts >= 3) {
          setError('Invalid credentials. You have exceeded 3 failed attempts. Please reset your password.');
          setShowForgotPassword(true);
        } else {
          setError(`Sign in failed. ${3 - newFailedAttempts} attempt(s) remaining.`);
        }
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessfulLogin = () => {
    setFailedAttempts(0);
    setShowForgotPassword(false);
  };

  return (
    <AuthShell
      title={
        <>
          Welcome <span style={{ fontStyle: 'italic' }}>back</span>
        </>
      }
      subtitle="Sign in to continue your journey"
    >
      {error ? (
        <Box style={{ width: '100%', marginBottom: 16 }}>
          <Alert
            severity="error"
            sx={{
              width: '100%',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(211, 47, 47, 0.2)',
            }}
          >
            {error}
          </Alert>
        </Box>
      ) : null}

      {showForgotPassword ? (
        <Box style={{ width: '100%', marginBottom: 16 }}>
          <Alert
            severity="warning"
            sx={{
              width: '100%',
              borderRadius: 2,
              border: '1px solid rgba(212, 168, 71, 0.25)',
              bgcolor: 'rgba(201, 162, 39, 0.1)',
              color: '#5c4a32',
              '& .MuiAlert-icon': { color: '#b8860b' },
            }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate('/forgot-password', { state: { identifier } })}
                sx={{
                  borderColor: 'rgba(139, 105, 20, 0.35)',
                  color: '#7a5c1a',
                  '&:hover': { bgcolor: 'rgba(201, 162, 39, 0.12)' },
                }}
              >
                Reset Password
              </Button>
            }
          >
            Too many failed login attempts. Please reset your password.
          </Alert>
        </Box>
      ) : null}

      <Box component="form" onSubmit={handleSubmit} sx={authFormSurfaceSx}>
        <TextField
          required
          fullWidth
          id="identifier"
          label="Email or mobile number"
          name="identifier"
          autoComplete="username"
          value={identifier}
          onChange={(e) => {
            const next = formatLoginIdentifierInput(e.target.value);
            setIdentifier(next);
            setIdentifierError(getLoginIdentifierValidationError(next));
          }}
          error={!!identifierError}
          helperText={identifierError || ' '}
          placeholder="you@example.com or 10-digit mobile"
          type={identifier.includes('@') ? 'email' : 'text'}
          inputProps={
            identifier.includes('@')
              ? undefined
              : { inputMode: 'numeric', maxLength: 10, pattern: '[0-9]*' }
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {identifier.includes('@') ? (
                  <AlternateEmailIcon sx={{ color: 'rgba(46, 38, 28, 0.45)' }} />
                ) : (
                  <PhoneIcon sx={{ color: 'rgba(46, 38, 28, 0.45)' }} />
                )}
              </InputAdornment>
            ),
          }}
        />

        <TextField
          required
          fullWidth
          id="password"
          label="Password"
          name="password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          sx={{ mt: 1.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  sx={{ color: '#b8860b' }}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Typography variant="body2" sx={{ color: 'rgba(46, 38, 28, 0.58)' }}>
            <span style={{ opacity: 0.9 }}>Forgot password?</span>{' '}
            <Link
              component={RouterLink}
              to="/forgot-password"
              underline="hover"
              sx={{ color: '#9a7318', fontWeight: 'bold' }}
            >
              Reset
            </Link>
          </Typography>
          <Button
            type="button"
            variant="text"
            onClick={() => navigate('/forgot-password')}
            sx={{
              color: 'rgba(46, 38, 28, 0.5)',
              textTransform: 'none',
              '&:hover': { color: '#9a7318', backgroundColor: 'rgba(201, 162, 39, 0.06)' },
            }}
          >
            Continue with OTP
          </Button>
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            mt: 2,
            py: 1.5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #c49a35 0%, #d4a847 40%, #e8c96e 100%)',
            color: '#1a1000',
            fontSize: '1.05rem',
            fontWeight: 'bold',
            textTransform: 'none',
            boxShadow: '0 8px 28px rgba(212, 168, 71, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #e8c96e 0%, #d4a847 60%, #c49a35 100%)',
              boxShadow: '0 8px 28px rgba(212, 168, 71, 0.4)',
              transform: 'translateY(-2px)',
            },
            '&:disabled': {
              background: 'linear-gradient(135deg, #c49a35 0%, #d4a847 40%, #e8c96e 100%)',
              opacity: 0.7,
            },
            transition: 'all 0.3s ease',
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
        </Button>

        <Box textAlign="center" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ color: 'rgba(46, 38, 28, 0.58)' }}>
            New to {APP_DISPLAY_NAME}?{' '}
            <Link
              component={RouterLink}
              to="/signup"
              variant="body2"
              sx={{
                color: '#9a7318',
                fontWeight: 'bold',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Create an account
            </Link>
          </Typography>
        </Box>
      </Box>
    </AuthShell>
  );
};

export default Login;
